import React, { useEffect, useState } from 'react';
import { useNotification } from 'src/hooks/useNotification';
import { Button } from 'src/components/ui/button';
import { useDeleteKnowledgeBaseMutation } from 'src/services/knowledgeBaseService';
import {
  baseApi,
  useGetAllProjectsQuery,
  useUnassignProjectKnowledgeBaseMutation,
} from 'src/services';
import { clsx } from 'clsx';
import { ProductsType, ProductsTypeBack } from 'src/constants/products';
import { PATH_DASHBOARD } from 'src/constants/spa-routes';
import { useDispatch } from 'react-redux';
import { useLoader } from 'src/provider/LoaderProvider';

export const DeleteAiKnowledgeBaseForm = ({ close = () => {}, knowledgeBase }) => {
  const [isLoading, setIsLoading] = useState(false);
  const notification = useNotification();
  const dispatch = useDispatch();
  const loader = useLoader();
  const [deleteKnowledgeBase, { isLoading: isLoadingDeleteKB }] = useDeleteKnowledgeBaseMutation();
  const { data: projects } = useGetAllProjectsQuery(undefined) as any;
  const [unassignKB, { isLoading: isLoadingUnassignKB }] =
    useUnassignProjectKnowledgeBaseMutation();

  useEffect(() => {
    if (isLoadingDeleteKB || isLoadingUnassignKB) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [isLoadingDeleteKB, isLoadingUnassignKB]);

  const deleteHandler = () => {
    deleteKnowledgeBase({ knowledge_base_id: knowledgeBase.id }).then((res: any) => {
      if (res?.data?.status === 'ok') {
        close();
        notification.success('Knowledge Base was successfully deleted!');
      }
    });
  };

  const disconnectHandler = () => {
    const promises = [];
    const ids = knowledgeBase.projects.map((project) => project.id);
    loader.show();
    ids.forEach((id) => {
      const project = projects.find((project) => project.id === id);
      if (project) {
        const promise = unassignKB({
          project_type: ProductsTypeBack[project.project_type],
          projectId: project.id,
          body: [knowledgeBase.id],
        });

        promises.push(promise);
      }
    });

    Promise.allSettled(promises).then(() => {
      dispatch(baseApi.util.invalidateTags(['KnowledgeBase']));
      loader.hide();
    });
  };

  return (
    <>
      <div>
        <div className="flex items-center gap-3.5">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10 19C14.9706 19 19 14.9706 19 10C19 5.02944 14.9706 1 10 1C5.02944 1 1 5.02944 1 10C1 14.9706 5.02944 19 10 19Z"
              stroke="#FF5151"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M10 14H10.01V14.01H10V14Z"
              stroke="#FF5151"
              strokeWidth="2.25"
              strokeLinejoin="round"
            />
            <path
              d="M10 10V6"
              stroke="#FF5151"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {Boolean(knowledgeBase.projects.length) ? (
            <div className={'flex flex-col'}>
              <p className="text-delete text-base font-medium">
                This knowledge base cannot be deleted because it is used by active AI Agents.
              </p>
            </div>
          ) : (
            <p className="text-delete text-base font-medium">
              Are you sure you want to delete the knowledge base? Deletion is irreversible.
            </p>
          )}
        </div>
        {Boolean(knowledgeBase.projects.length) && (
          <div className={'flex items-center gap-3.5 mt-2.5'}>
            <span className={'w-5'}></span>
            <div>
              <p className={'text-base font-medium text-ebony_opacity_50'}>
                Disconnect this knowledge base from the following AI Agents:
              </p>
              <ul className={'list-disc pl-4 mt-2.5'}>
                {knowledgeBase.projects.map((project) => {
                  const projectData = projects.find((item) => item.id === project.id);

                  if (!projectData) {
                    return null;
                  }

                  const url =
                    projectData.project_type === ProductsType.customer
                      ? `${PATH_DASHBOARD.PROJECTS}/${projectData.id}/control-desk?type=${projectData.project_type}`
                      : `${PATH_DASHBOARD.PROJECTS}/${projectData.id}?type=${projectData.project_type}`;

                  return (
                    <li
                      className={clsx(
                        'text-base/[170%] font-medium text-ebony_opacity_50 ',
                        projectData && 'hover:text-ebony',
                      )}
                      key={project.id}
                    >
                      {projectData ? (
                        <a className={' underline '} href={url}>
                          {project.name || 'no name'} →
                        </a>
                      ) : (
                        <p>{project.name || 'no name'}</p>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}
      </div>
      <div className="flex justify-center mt-5">
        <div className="flex gap-2.5">
          <Button variant="outline" type="button" className="min-w-24" onClick={close}>
            Cancel
          </Button>
          {Boolean(knowledgeBase.projects.length) ? (
            <Button autoFocus onClick={disconnectHandler} className="min-w-24">
              {isLoading ? 'Loading...' : 'Disconnect'}
            </Button>
          ) : (
            <Button autoFocus onClick={deleteHandler} className="min-w-24">
              {isLoading ? 'Loading...' : 'Delete'}
            </Button>
          )}
        </div>
      </div>
    </>
  );
};
